import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, ArrowRight, Loader2, CreditCard, Shield, X, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { toast } from "sonner";
import { SectionEyebrow } from "./Primitives";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card"); // card or upi
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [upiId, setUpiId] = useState("");
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [activatingCoupon, setActivatingCoupon] = useState(false);

  const handleActivateCoupon = async (e) => {
    if (e) e.preventDefault();
    if (!couponCode) return;
    if (!user) {
      toast.error("Please log in to redeem a coupon code.");
      navigate("/login");
      return;
    }
    setActivatingCoupon(true);
    try {
      const res = await api.post("/subscription/activate-coupon", {
        coupon_code: couponCode
      });
      if (res.data.success) {
        toast.success(res.data.message || "Welcome to STUDLYF Pro! Your Pro access has been activated successfully and is valid for 30 days.");
        setCouponCode("");
        fetchSubscription();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Invalid coupon code.";
      toast.error(msg);
    } finally {
      setActivatingCoupon(false);
    }
  };

  // Fetch subscription details
  const fetchSubscription = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get("/subscription");
      setSub(res.data);
    } catch (e) {
      console.error("Error fetching subscription:", e);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleFreeTrial = async () => {
    if (!user) {
      toast.error("Please log in or sign up to activate a plan.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/subscription/free");
      if (res.data.success) {
        toast.success("Free trial activated successfully! Enjoy 30 days of standard access.");
        fetchSubscription();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not activate free trial.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = () => {
    if (!user) {
      toast.error("Please log in or sign up to upgrade to Pro.");
      return;
    }
    setModalOpen(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (paymentMethod === "card") {
      if (!cardNumber || !cardExpiry || !cardCvv) {
        toast.error("Please fill in all card details.");
        return;
      }
    } else {
      if (!upiId) {
        toast.error("Please enter a valid UPI ID.");
        return;
      }
    }

    setProcessing(true);
    // Simulate Razorpay transaction delays
    setTimeout(async () => {
      try {
        const res = await api.post("/subscription/upgrade");
        if (res.data.success) {
          setSuccess(true);
          toast.success("Payment Successful! Pro Plan activated.");
          fetchSubscription();
          setTimeout(() => {
            setModalOpen(false);
            setSuccess(false);
            setProcessing(false);
            // Redirect to dashboard on upgrade success
            navigate("/workspace/dashboard");
          }, 1500);
        }
      } catch (err) {
        toast.error("Payment failed. Please try again.");
        setProcessing(false);
      }
    }, 1800);
  };

  // Compare Table Data
  const compareRows = [
    { label: "AI Workspace", free: "Limited", pro: "Unlimited" },
    { label: "Strategy", free: "Standard Frameworks", pro: "All Premium Systems" },
    { label: "Marketing", free: "Basic Campaigns", pro: "Full GTM Campaigns" },
    { label: "Funding", free: "Basic Insights", pro: "Full Deck & Investor Scoring" },
    { label: "Templates", free: "Basic Startup Templates", pro: "All Presentation Slides" },
    { label: "Premium Templates", free: false, pro: true },
    { label: "Network Connector", free: "Limited Matches", pro: "Unlimited Connects" },
    { label: "Analytics", free: "Basic Dashboard", pro: "Advanced heatmaps & exports" },
    { label: "Export", free: false, pro: "PDF, PPTX, DOCX" },
    { label: "Priority Support", free: false, pro: true },
    { label: "AI Requests", free: "Limited", pro: "Unlimited" },
    { label: "Support", free: "Community Support", pro: "24/7 Dedicated Support" },
  ];

  return (
    <section id="pricing" className="relative py-20 lg:py-28 bg-transparent text-gray-900 overflow-hidden">
      <div className="max-w-[1600px] w-full mx-auto px-4 sm:px-6 md:px-10 lg:px-12">
        
        {/* Header Section */}
        <div className="text-center max-w-4xl lg:max-w-5xl mx-auto mb-16">
          <SectionEyebrow>Pricing Plans</SectionEyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900">
            Choose the <span className="brand-gradient-text">Perfect Plan</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-600 leading-relaxed">
            Start free and upgrade when you're ready to scale your startup.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-20">
          
          {/* FREE CARD */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl p-8 md:p-10 border border-gray-100/70 bg-white/70 backdrop-blur-md shadow-lg flex flex-col justify-between"
            data-testid="pricing-card-free"
          >
            <span className="absolute -top-3.5 left-8 text-[11px] font-bold tracking-widest uppercase px-4 py-1.5 rounded-full bg-gray-950 text-white shadow-sm">
              Get Started
            </span>

            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Free Plan</p>
              
              {/* Expiry / Remaining Days indicators */}
              {sub?.plan_name === "Free" && (
                <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#7C3AED]/10 text-[#7C3AED]">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {sub.status === "expired" ? "Trial Expired" : `${sub.remaining_days} days remaining`}
                </div>
              )}

              <div className="mt-6 flex items-baseline gap-1.5">
                <span className="font-display text-5xl font-extrabold text-gray-950">₹0</span>
                <span className="text-gray-500 text-sm">/ 30 Days</span>
              </div>

              <ul className="mt-8 space-y-4">
                {[
                  "AI Workspace (Limited)",
                  "Strategy Generator",
                  "Marketing Generator",
                  "Funding Generator",
                  "Basic Templates",
                  "Limited AI Requests",
                  "Limited Network Connector",
                  "Basic Analytics",
                  "Community Support"
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-gray-700">
                    <Check className="w-4 h-4 mt-0.5 text-green-500 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              {sub?.plan_name === "Free" && sub.status === "expired" ? (
                <button
                  onClick={handleUpgradeClick}
                  className="w-full rounded-full py-4 text-sm font-bold bg-[#7C3AED] hover:bg-[#6D28D9] text-white transition-all shadow-md flex items-center justify-center gap-2"
                >
                  Upgrade to Pro <ArrowRight className="w-4 h-4" />
                </button>
              ) : sub?.plan_name === "Free" ? (
                <button
                  disabled
                  className="w-full rounded-full py-4 text-sm font-bold bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                >
                  Trial Active
                </button>
              ) : sub?.plan_name === "STUDLYF Pro" ? (
                <button
                  disabled
                  className="w-full rounded-full py-4 text-sm font-bold bg-gray-50 text-gray-300 border border-gray-100 cursor-not-allowed"
                >
                  Trial Expired
                </button>
              ) : (
                <button
                  onClick={handleFreeTrial}
                  disabled={loading}
                  className="w-full rounded-full py-4 text-sm font-bold bg-gray-950 text-white hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
                  data-testid="pricing-cta-free"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start Free Trial"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>

          {/* PRO CARD */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl p-8 md:p-10 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white shadow-[0_24px_50px_-15px_rgba(124,58,237,0.4)] flex flex-col justify-between transform lg:-translate-y-4 border border-transparent"
            data-testid="pricing-card-pro"
          >
            <span className="absolute -top-3.5 left-8 text-[11px] font-bold tracking-widest uppercase px-4 py-1.5 rounded-full bg-white text-[#7C3AED] shadow-sm flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 fill-[#7C3AED]" /> Most Popular
            </span>

            <div>
              <p className="text-sm font-semibold text-white/90 uppercase tracking-wider">STUDLYF Pro</p>
              
              <div className="mt-6 flex items-baseline gap-1.5">
                <span className="font-display text-5xl font-extrabold text-white">₹499</span>
                <span className="text-white/80 text-sm">/ One-time payment</span>
              </div>

              <ul className="mt-8 space-y-4">
                {[
                  "Unlimited AI Workspace",
                  "Unlimited Strategy Generation",
                  "Unlimited Marketing Generation",
                  "Unlimited Funding Generation",
                  "Unlimited Templates",
                  "Premium Startup Templates",
                  "Network Connector",
                  "AI Recommendations",
                  "Advanced Analytics",
                  "Priority Support",
                  "Export PDF, PPTX, DOCX",
                  "Future Premium Features"
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/90">
                    <Check className="w-4 h-4 mt-0.5 text-white bg-white/20 rounded-full p-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              {sub?.plan_name === "STUDLYF Pro" ? (
                <button
                  disabled
                  className="w-full rounded-full py-4 text-sm font-bold bg-white/20 text-white cursor-not-allowed border border-white/10"
                >
                  Current Plan
                </button>
              ) : (
                <button
                  onClick={handleUpgradeClick}
                  className="w-full rounded-full py-4 text-sm font-bold bg-white text-gray-900 hover:bg-gray-50 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-md flex items-center justify-center gap-2"
                  data-testid="pricing-cta-pro"
                >
                  Upgrade to Pro <ArrowRight className="w-4 h-4 text-[#7C3AED]" />
                </button>
              )}
            </div>
          </motion.div>

        </div>

        {/* Have a Pro Access Code? Activation Section */}
        <div className="max-w-2xl mx-auto my-12 p-8 rounded-[28px] border border-[#7C3AED]/20 bg-gradient-to-br from-[#7C3AED]/5 via-white to-[#EC4899]/5 shadow-sm text-center">
          <h3 className="font-display text-2xl font-extrabold text-gray-950">Have a Pro Access Code?</h3>
          <p className="text-xs text-gray-600 mt-1.5 mb-6">Enter your coupon code below to unlock 30 days of full STUDLYF Pro access.</p>
          
          <form onSubmit={handleActivateCoupon} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="text"
              placeholder="Enter Coupon Code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase().trim())}
              disabled={activatingCoupon}
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-xs outline-none bg-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 uppercase font-mono tracking-wider text-gray-900 shadow-2xs"
            />
            <button
              type="submit"
              disabled={activatingCoupon || !couponCode}
              className="px-6 py-3 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white hover:opacity-90 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-2 shrink-0 disabled:opacity-50"
            >
              {activatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 fill-current" />}
              Activate
            </button>
          </form>

          {/* Active Subscription Details */}
          {sub && sub.plan_name === "STUDLYF Pro" && (
            <div className="mt-6 pt-6 border-t border-[#7C3AED]/15 grid grid-cols-2 sm:grid-cols-4 gap-4 text-left text-xs bg-white/90 rounded-2xl p-4 border border-gray-150">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Current Plan</p>
                <p className="font-bold text-gray-900 mt-0.5">{sub.plan_name}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Coupon</p>
                <p className="font-bold text-gray-900 mt-0.5 font-mono">{sub.coupon_code || "STUDLYF499"}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</p>
                <p className="font-bold text-green-600 mt-0.5 capitalize">{sub.status}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Remaining</p>
                <p className="font-bold text-gray-900 mt-0.5">{sub.remaining_days} Days</p>
              </div>
            </div>
          )}
        </div>

        {/* Feature Comparison Table */}
        <div className="max-w-4xl mx-auto border border-gray-150 rounded-[24px] bg-white overflow-hidden shadow-sm mt-20">
          <div className="p-6 md:p-8 bg-gray-50/50 border-b border-gray-150">
            <h3 className="text-lg font-bold text-gray-950 font-display">Plan Feature Comparison</h3>
            <p className="text-xs text-gray-500 mt-1">A detailed look at what is included in each option.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-150 bg-gray-50/20 text-xs font-bold uppercase tracking-wider text-gray-500">
                  <th className="py-4 px-6">Features</th>
                  <th className="py-4 px-6 text-center w-36">Free</th>
                  <th className="py-4 px-6 text-center w-36">Pro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-150 text-sm text-gray-700">
                {compareRows.map((row) => (
                  <tr key={row.label} className="hover:bg-gray-50/40 transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-900">{row.label}</td>
                    <td className="py-4 px-6 text-center">
                      {typeof row.free === "string" ? (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded bg-gray-100 text-gray-600">{row.free}</span>
                      ) : row.free ? (
                        <Check className="w-4.5 h-4.5 mx-auto text-green-500" />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-center font-semibold">
                      {typeof row.pro === "string" ? (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded bg-[#7C3AED]/10 text-[#7C3AED]">{row.pro}</span>
                      ) : row.pro ? (
                        <Check className="w-4.5 h-4.5 mx-auto text-green-500" />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* PAYMENT SIMULATOR MODAL */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !processing && setModalOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-xs"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ scale: 0.95, y: 15, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 15, opacity: 0 }}
              className="relative w-full max-w-md rounded-[28px] bg-white p-6 md:p-8 shadow-2xl border border-gray-100 text-gray-900 overflow-hidden z-10"
            >
              
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-bold font-display text-gray-950 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#7C3AED]" /> Payment Simulator
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Secure payment powered by Razorpay abstraction</p>
                </div>
                {!processing && (
                  <button
                    onClick={() => setModalOpen(false)}
                    className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {success ? (
                <div className="py-12 flex flex-col items-center justify-center text-center space-y-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", damping: 12 }}
                    className="w-16 h-16 rounded-full bg-green-500 text-white flex items-center justify-center shadow-lg"
                  >
                    <Check className="w-8 h-8 stroke-[3]" />
                  </motion.div>
                  <h4 className="text-xl font-bold font-display text-gray-950">Subscription Activated!</h4>
                  <p className="text-sm text-gray-500">Unlocking Pro features and returning to your dashboard...</p>
                </div>
              ) : (
                <form onSubmit={handlePaymentSubmit} className="space-y-5">
                  
                  {/* Amount Badge */}
                  <div className="bg-[#7C3AED]/5 border border-[#7C3AED]/10 rounded-2xl p-4 flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Amount Due</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">STUDLYF Pro Plan</p>
                    </div>
                    <span className="text-2xl font-extrabold text-[#7C3AED] font-display">₹499</span>
                  </div>

                  {/* Payment Method Selector */}
                  <div className="grid grid-cols-2 gap-3 p-1 bg-gray-100 rounded-xl">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("card")}
                      className={`py-2 text-xs font-semibold rounded-lg transition-all ${
                        paymentMethod === "card" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Credit/Debit Card
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("upi")}
                      className={`py-2 text-xs font-semibold rounded-lg transition-all ${
                        paymentMethod === "upi" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      UPI ID
                    </button>
                  </div>

                  {/* Card Fields */}
                  {paymentMethod === "card" ? (
                    <div className="space-y-3.5 animate-in fade-in-20 duration-150">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Card Number</label>
                        <input
                          type="text"
                          required
                          value={cardNumber}
                          onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, "").slice(0, 16))}
                          placeholder="4111 2222 3333 4444"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/10 outline-none text-sm font-medium transition"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Expiry Date</label>
                          <input
                            type="text"
                            required
                            value={cardExpiry}
                            onChange={(e) => setCardExpiry(e.target.value.slice(0, 5))}
                            placeholder="MM/YY"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/10 outline-none text-sm font-medium text-center transition"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">CVV</label>
                          <input
                            type="password"
                            required
                            value={cardCvv}
                            onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, "").slice(0, 3))}
                            placeholder="•••"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/10 outline-none text-sm font-medium text-center transition"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* UPI Field */
                    <div className="space-y-3.5 animate-in fade-in-20 duration-150">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">UPI ID</label>
                        <input
                          type="text"
                          required
                          value={upiId}
                          onChange={(e) => setUpiId(e.target.value)}
                          placeholder="username@okaxis"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/10 outline-none text-sm font-medium transition"
                        />
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <button
                    type="submit"
                    disabled={processing}
                    className="w-full rounded-full py-4 text-sm font-bold bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-md flex items-center justify-center gap-2 transition disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed mt-2"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Processing Transaction...
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4" /> Pay ₹499 Now
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </section>
  );
}
